import { Box, Flex, Heading, Hide, Show, SimpleGrid, Text, useBreakpointValue } from "@chakra-ui/react"
import { pages, searchNavigationParams } from "../../../../../common/constants/constants"
import { tabs } from "../../../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import { FhResponseData } from "../../../../../common/types/search"
import { getProductTileAttributes } from "../../../../../common/utils/product-utils"
import { getModifiedQueryParam } from "../../../../../common/utils/search-term-utils"
import { getSparePartTileAttributes } from "../../../../../common/utils/spare-parts-utils"
import { ChakraNextLinkButton } from "../../../../shared/ChakraNextLinkButton"
import { CArrowRight } from "../../../../shared/Icons"
import { BasicSearchTile } from "../../../../shared/Tiles/BasicSearchTile"

type Props = {
  items: FhResponseData[]
  numberOfResults: number
  originalSearchTerm: string
  partialSearchTerm: string
  rid: { [key: string]: string }
}

const PartialResultsSingleTerm = ({ items, numberOfResults, originalSearchTerm, partialSearchTerm, rid }: Props) => {
  const { t } = useBrandedTranslation()
  const originalSearchTermWords = originalSearchTerm.split(" ")
  const partialSearchTermWords = partialSearchTerm.split(" ")

  const normalizedItems =
    items?.map((item) =>
      item?.id?.includes(tabs.PRO.toLocaleLowerCase())
        ? getProductTileAttributes(item)
        : getSparePartTileAttributes(item)
    ) ?? []

  /* Responsive styles for border for product results grid */
  const numberOfGridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 })
  const productItemsBorderStyle = {
    [`& >:nth-of-type(${numberOfGridColumns}n)`]: { _after: { content: "none" } },
    [`& >:last-of-type`]: { borderBottom: "none" },
    "& > article": {
      borderBottom: `${numberOfGridColumns === 1 ? "1px solid" : "none"}`,
      borderColor: "gray.300",
      pb: "4",
      mt: "4",
    },
  }

  return (
    <Box as="section" mb={{ base: "6", md: "8" }}>
      <Flex align="center" justify="space-between" mb={{ base: "6", md: "8" }}>
        <Heading
          as="h2"
          fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
          fontWeight="light"
          letterSpacing="0"
          lineHeight={{ base: "7", sm: "7.5", md: "8" }}
        >
          <Text as="span">
            {numberOfResults} {t("search-results-for", { count: numberOfResults })}
          </Text>
          {originalSearchTermWords.map((word: string) => {
            const isMatch = partialSearchTermWords.includes(word)
            return (
              <Text
                as="span"
                fontWeight={isMatch ? "bold" : "light"}
                textDecoration={isMatch ? "none" : "line-through"}
                key={word}
              >
                {" "}
                {word}
              </Text>
            )
          })}
        </Heading>
        <Show above="xs">
          <ChakraNextLinkButton
            label={t("nav-see-all")}
            color="black"
            leftIcon={<CArrowRight w="5" h="4" />}
            iconSpacing="2.5"
            href={`/${pages.SEARCH}?${searchNavigationParams.SEARCH_TERM}=${getModifiedQueryParam(partialSearchTerm)}`}
          />
        </Show>
      </Flex>
      <SimpleGrid templateColumns={`repeat(${numberOfGridColumns}, 1fr)`} sx={productItemsBorderStyle}>
        {normalizedItems.map((item) => (
          <BasicSearchTile key={item.id} item={item} hasAnalytics={true} rid={rid} />
        ))}
      </SimpleGrid>
      <Hide above="xs">
        <ChakraNextLinkButton
          label={t("nav-see-all")}
          color="black"
          border="1px solid"
          variant="secondary"
          width="100%"
          mb="4"
          mt="4"
          href={`/${pages.SEARCH}?${searchNavigationParams.SEARCH_TERM}=${getModifiedQueryParam(partialSearchTerm)}`}
        />
      </Hide>
    </Box>
  )
}

export default PartialResultsSingleTerm
